import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router';

import { Image } from '#/components/Image/Image';
import { useAppConfig } from '#/config/AppConfigContext';
import { useEnvironment, useUser } from '#/contexts/UserEnvironmentContext';
import { useI18n } from '#/i18n/hooks/useI18n';
import { cmsApiSubscribeToSubsite } from '#/modules/cms/api/cmsApiSubscribeToSubsite';
import type { CmsApiSubsiteId } from '#/modules/cms/api/types/CmsApiTypedId';
import { useCmsSubscribedSubsitesIncludes } from '#/modules/cms/zustand-stores/cmsSubscribedSubsitesStore';
import { getStaticImageSrc } from '#/utils/getImageSrc';
import { getParam } from '#/utils/url/getParam';
import { showModal } from '#/zustand-stores/modalStore';

export function useCmsSubscribeToSubsite(subsiteId: CmsApiSubsiteId) {
  const { appConfig } = useAppConfig();
  const { i18n } = useI18n();
  const location = useLocation();

  const { isSubscribed } = useCmsSubscribedSubsitesIncludes(subsiteId);
  const [submitting, setSubmitting] = useState(false);

  const { user } = useUser();
  const { environment } = useEnvironment();
  const isUserLoaded = Boolean(environment);

  const subscribe = useCallback(async () => {
    if (user) {
      setSubmitting(true);
      await cmsApiSubscribeToSubsite(subsiteId);
      setSubmitting(false);

      showModal('SUBSCRIBED_TO_SUBSITE', {});
    } else {
      showModal('EMAIL_SIGNUP', {
        tmpStorage: {
          type: 'subscribedSubsite',
          subscribedSubsiteId: subsiteId,
        },
        title: (
          <div style={{ textAlign: 'center' }}>
            {i18n('Sign up to subscribe to our newsletter!')}
          </div>
        ),
        image: (
          <div style={{ textAlign: 'center' }}>
            <Image
              src={getStaticImageSrc(
                'subscribe-signup.svg',
                appConfig.idealist.staticImgPath,
              )}
              alt=""
            />
          </div>
        ),
      });
    }
  }, [i18n, user, subsiteId, appConfig]);

  useEffect(() => {
    if (isUserLoaded) {
      const initSubscribe = getParam(location, 'subscribe');

      if (initSubscribe) {
        void subscribe();
      }
    }
  }, [isUserLoaded, location, subscribe]);

  return {
    subscribe,
    isSubscribed,
    submitting,
  };
}
